import EmptyState from '@app/src/components/EmptyState'
import { EmptyStateVariant } from '@app/src/components/EmptyState/EmptyState'
import { useGroupBySelector } from '@app/src/context/GroupBySelectorContext'
import { useGetApiQueryFilters } from '@app/src/hooks/queryFilters'
import { useStatisticsResultByGroup } from '@app/src/hooks/useStatisticsResultByGroup'
import { ChartType } from '@app/src/pages/Assessments/ChartTypeSelector'
import { dataHubAllowedFilters } from '@app/src/pages/ResourceCollection/Collections/DataHub/DataInsights'
import DataHubSummaryChart from '@app/src/pages/ResourceCollection/Collections/DataHub/DataInsights/Charts/DataHubSummaryChart'
import { NumberMultiGroupChart } from '@app/src/pages/ResourceCollection/Collections/DataHub/DataInsights/Charts/NumberMultiGroupChart'
import { OptionSingleGroupPieChart } from '@app/src/pages/ResourceCollection/Collections/DataHub/DataInsights/Charts/OptionSingleGroupPieChart'
import { AnswersGroupingType } from '@app/src/pages/ResourceCollection/Filters/StatisticsConstants'
import { QuestionTypes } from '@app/src/wf-constants'
import BarChartIcon from '@mui/icons-material/BarChart'
import type ReactEChartsCore from 'echarts-for-react/lib/core'
import React from 'react'
import { useIntl } from 'react-intl'
import type { StatisticsForQuestionPerGroup } from '../../../DataHubScene'
import { NumberMultiGroupLineChart } from './NumberMultiGroupLineChart'
import { OptionMultiGroupChart } from './OptionMultiGroupChart'

interface DataHubInsightsCardProps {
  statistics: StatisticsForQuestionPerGroup
  eChartsRef: React.MutableRefObject<ReactEChartsCore | null>
  selectedChartType: ChartType
  isFullWidth?: boolean
  periodNames?: string[]
}

const DataInsightsChart: React.FC<DataHubInsightsCardProps> = ({
  statistics,
  eChartsRef,
  selectedChartType,
  isFullWidth,
  periodNames,
}) => {
  const { formatMessage } = useIntl()

  const height = `500px`
  const { groupBy } = useGroupBySelector()
  const userFilters = useGetApiQueryFilters(dataHubAllowedFilters)

  const { periodStatisticSummaryResult, spendStatisticSummaryResult } = useStatisticsResultByGroup(
    statistics,
    groupBy,
    userFilters,
  )

  const getStatisticsSummaryResult = () => {
    switch (groupBy) {
      case AnswersGroupingType.SpendClassification:
        return spendStatisticSummaryResult

      case AnswersGroupingType.PeriodName:
      default:
        return periodStatisticSummaryResult
    }
  }

  const statisticsSummaryResult = getStatisticsSummaryResult()

  const noData = statisticsSummaryResult.resultForGroups.every(
    r => r.companiesAnswered === 0 && r.companiesNotAnswered === 0 && r.companiesAnsweredDontHaveThis === 0,
  )
  if (noData)
    return (
      <EmptyState
        sx={{ height: height }}
        title={formatMessage({ id: 'statistics.dataInsights.noDataForPeriod' })}
        iconComponent={BarChartIcon}
        variant={EmptyStateVariant.Small}
      />
    )

  switch (statistics.questionType) {
    case QuestionTypes.Options:
      if (selectedChartType === ChartType.Pie)
        return (
          <OptionSingleGroupPieChart
            statistics={statistics}
            statisticsSummary={periodStatisticSummaryResult}
            height={height}
            echartsRef={eChartsRef}
          />
        )

      return (
        <OptionMultiGroupChart
          statistics={statistics}
          statisticsSummary={periodStatisticSummaryResult}
          height={height}
          echartsRef={eChartsRef}
          selectedChartType={selectedChartType}
          isFullWidth={isFullWidth}
        />
      )
    case QuestionTypes.Number:
      if (selectedChartType === ChartType.Line)
        return (
          <NumberMultiGroupLineChart
            statistics={statistics}
            statisticsSummary={periodStatisticSummaryResult}
            unit={statistics.unit}
            height={height}
            echartsRef={eChartsRef}
          />
        )

      return (
        <NumberMultiGroupChart
          statistics={statistics}
          height={height}
          echartsRef={eChartsRef}
          unit={statistics.unit}
          selectedChartType={selectedChartType}
        />
      )
    default:
      return (
        <DataHubSummaryChart
          statistics={statisticsSummaryResult}
          echartsRef={eChartsRef}
          selectedChartType={selectedChartType}
          groupBy={groupBy}
          periodNames={periodNames ?? []}
        />
      )
  }
}

export default DataInsightsChart
